<script setup lang="ts">
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import SafeHtml from "@/components/SafeHtml.vue";
import SaveButton from "@/components/SaveButton.vue";
import { useRedirectTargets } from "@/composables/useRedirectTargets";
import { GoneError } from "@/gateways/apiClient";
import LoggedOut from "@/layouts/LoggedOut.vue";
import type { redirectSchema } from "@/router/dtos";
import { useGetSettings } from "@/settings/settingsCrud";
import { computed, ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import * as z from "zod/v4-mini";
import {
  useActivatePendingUser,
  useGetPendingUserPreview,
} from "./pendingUserCrud";

const { token, redirect } = defineProps<{
  token: string;
  redirect: z.infer<typeof redirectSchema>;
}>();

const router = useRouter();

const redirectTarget = useRedirectTargets(redirect);

const { pendingUser, getPendingUserStatus } = useGetPendingUserPreview(token);

const {
  activatePendingUser,
  activatePendingUserStatus,
  error: activatePendingUserError,
} = useActivatePendingUser(token, {
  onSuccess: () => {
    router.push(redirectTarget.value ?? { name: "home" });
  },
});

// If the invitation/signup has gone, after the user has loaded the page
// and before they click the accept button, the API will return a 410 Gone error.
// In this case, we want to show a specific message.
const pendingUserGone = computed(
  () => activatePendingUserError.value instanceof GoneError,
);

const name: Ref<string> = ref("");

const handleActivate = () => {
  activatePendingUser({ name: name.value });
};

const { settings, getSettingsStatus } = useGetSettings();
</script>

<template>
  <LoggedOut>
    <HandleQueryStatus :query-status="getPendingUserStatus">
      <template v-if="pendingUser == null || pendingUserGone">
        <h1>Link nicht gefunden</h1>
        <p>
          Dieser Link ist ungültig oder bereits abgelaufen. Bitte erstelle den
          Account erneut oder wende dich an die Person, die dich eingeladen hat.
        </p>
      </template>
      <template v-else>
        <h1>Account erstellen</h1>
        <form class="column" @submit.prevent="handleActivate">
          <p>Willkommen beim Kiezkompass!</p>
          <p>
            Du erstellst einen Account für
            <strong>{{ pendingUser.email }}</strong
            >.
          </p>

          <p>
            <label for="name">Name</label>
            <input id="name" v-model.trim="name" type="text" required />
          </p>

          <HandleQueryStatus :query-status="getSettingsStatus">
            <SafeHtml :html="settings?.userConsentText" />
          </HandleQueryStatus>

          <SaveButton
            class="fullwidth"
            :saving-status="activatePendingUserStatus"
          >
            Account erstellen
          </SaveButton>
        </form>
      </template>
    </HandleQueryStatus>
  </LoggedOut>
</template>

<style scoped>
:deep(.fullwidth) {
  width: 100%;
  justify-content: center;
}
</style>
